import { forwardRef } from '@heroui/system';
import { Textarea, TextAreaProps } from '@heroui/react';
import { ClearButton } from './ClearButton';
import { Counter } from './Counter';

interface CustomTextareaProps extends Omit<TextAreaProps, 'ref'> {
  onClear?: () => void;
}

const CustomTextarea = forwardRef<'textarea', CustomTextareaProps>((props, ref) => {
  const {
    startContent,
    classNames,
    label,
    value,
    onChange,
    onClear,
    isClearable = true,
    maxLength,
    ...restProps
  } = props;

  const textLength = String(value || '').length;

  const isInvalid = !!maxLength && textLength > maxLength;

  const textareaProps = restProps as Omit<TextAreaProps, 'ref' | 'label' | 'value' | 'onChange' | 'onClear'>;

  const handleClear = () => {
    onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
    onClear?.();
  };

  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={onChange}
      isInvalid={isInvalid || props.isInvalid}
      maxLength={undefined}
      classNames={{
        base: 'tmdx-input group scroll scroll-sm',
        inputWrapper: [
          'bg-base-60',
          'group-data-[focus=true]:bg-base-70',
          'group-data-[invalid=true]:bg-red-80',
          'tmdx-input__wrapper',
          'group-data-[invalid=true]:bg-base-60!',
          'group-data-[invalid=true]:group-data-[hover=true]:bg-base-60!',
          'group-data-[invalid=true]:border-1',
          'group-data-[invalid=true]:border-danger',
          'pe-1!',
          'rounded-lg',
          'transition-none!',
          !label && 'min-h-16 h-16',
          !!label && 'hasLabel',
          'shadow-none',
          'relative',
        ],
        input: [
          'text-base-5',
          'placeholder:text-base-30',
          'text-m-r!',
          'group-data-[invalid=true]:text-base-5!',
          'tmdx-input__item',
          !!maxLength && '!pb-4 !pr-8',
        ],
        label: [
          'text-base-20',
          'text-s-r!',
          'tmdx-input__label',
          'group-focus-within:text-base-10',
          'group-data-[invalid=true]:text-danger!',
        ],
        description: ['text-base-20', 'text-s-r', 'group-data-[invalid=true]:text-danger', 'form-item__description'],
        innerWrapper: ['bg-transparent'],
        clearButton: ['opacity-100!'],
      }}
      label={label}
      startContent={startContent}
      endContent={
        <>
          <ClearButton isVisible={isClearable && !!value} onClear={handleClear} hasCounter={!!maxLength} />
          <Counter current={textLength} max={maxLength} isInvalid={isInvalid} hasValue={!!value} />
        </>
      }
      spellCheck="false"
      {...textareaProps}
    />
  );
});

CustomTextarea.displayName = 'CustomTextarea';

export default CustomTextarea;
